import {
  User,
  Building2,
  Users,
  CreditCard,
  Menu,
  X,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SettingsSidebar.css';

export type SettingsSection = 'profile' | 'integrations' | 'team' | 'billing';

export interface SidebarItem {
  id: SettingsSection;
  label: string;
  icon: React.ElementType;
  badge?: string | number;
}

interface SettingsSidebarProps {
  activeSection: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
  teamMemberCount?: number;
  trialActive?: boolean;
  canViewOrgSettings: boolean;
  canViewBilling: boolean;
  orgRoleLabel?: string;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

/**
 * Org settings (display name, ID) are the first blocks inside Integrations —
 * sidebar uses one item: "Organization & Integrations".
 */
const allItems: SidebarItem[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'integrations', label: 'Organization & Integrations', icon: Building2 },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  activeSection,
  onSectionChange,
  teamMemberCount,
  trialActive,
  canViewOrgSettings,
  canViewBilling,
  orgRoleLabel,
  collapsed: collapsedProp,
  onCollapsedChange,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  const collapsed = collapsedProp ?? internalCollapsed;
  const setCollapsed = onCollapsedChange ?? setInternalCollapsed;

  const visibleItems = allItems.filter((item) => {
    if (item.id === 'integrations' && !canViewOrgSettings) return false;
    if (item.id === 'team' && !canViewOrgSettings) return false;
    if (item.id === 'billing' && !canViewBilling) return false;
    return true;
  });

  const getBadge = useCallback(
    (id: SettingsSection): string | number | undefined => {
      if (id === 'team' && teamMemberCount) return teamMemberCount;
      if (id === 'billing' && trialActive) return 'Trial';
      return undefined;
    },
    [teamMemberCount, trialActive]
  );

  const handleSelect = useCallback(
    (id: SettingsSection) => {
      onSectionChange(id);
      setMobileOpen(false);
    },
    [onSectionChange]
  );

  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: PointerEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen]);

  return (
    <>
      <button
        type="button"
        className="settings-sidebar__mobile-toggle"
        onClick={() => setMobileOpen(true)}
        aria-label="Open settings navigation"
      >
        <Menu size={20} />
      </button>

      {mobileOpen && <div className="settings-sidebar__overlay" />}

      <nav
        ref={sidebarRef}
        className={`settings-sidebar${collapsed ? ' settings-sidebar--collapsed' : ''}${mobileOpen ? ' settings-sidebar--mobile-open' : ''}`}
        aria-label="Settings navigation"
      >
        <div className="settings-sidebar__header">
          {!collapsed && (
            <>
              <span className="settings-sidebar__header-title">Settings</span>
              {orgRoleLabel && <span className="settings-sidebar__role-badge">{orgRoleLabel}</span>}
            </>
          )}
          <button
            type="button"
            className="settings-sidebar__mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close settings navigation"
          >
            <X size={18} />
          </button>
        </div>

        <ul className="settings-sidebar__list">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const badge = getBadge(item.id);
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`settings-sidebar__item${isActive ? ' settings-sidebar__item--active' : ''}`}
                  onClick={() => handleSelect(item.id)}
                  aria-current={isActive ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={18} className="settings-sidebar__item-icon" />
                  {!collapsed && <span className="settings-sidebar__item-label">{item.label}</span>}
                  {!collapsed && badge != null && (
                    <span className="settings-sidebar__item-badge">{badge}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="settings-sidebar__collapse-toggle"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </nav>
    </>
  );
};
